import React, { useEffect, useState } from "react";
import AppContainer from "../../common/app_container";
import { useProductAPI } from "@src/api/product_api";
import { Product, IndustryType, ProductFeatureType } from "@src/model/product";
import { useNavigate, useParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import Alert, { AlertProps } from "@src/common/alert";
import BackButton from "@src/common/components/back-button";
import { PlusIcon, TrashIcon } from "@heroicons/react/20/solid";

interface ProductEditProps {}

interface FeatureState {
  [key: string]: {
    is_active: boolean;
    feature_limit: number | null;
  };
}

const ProductEdit: React.FC<ProductEditProps> = () => {
  const { id } = useParams();
  const productAPI = useProductAPI();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [bulletPoints, setBulletPoints] = useState<string[]>([""]);
  const [features, setFeatures] = useState<FeatureState>({
    [ProductFeatureType.MULTI_USER]: { is_active: false, feature_limit: null },
    [ProductFeatureType.SOCIAL_MEDIA]: { is_active: false, feature_limit: null },
    [ProductFeatureType.VIDEOS]: { is_active: false, feature_limit: null },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Product>();

  const onSubmit: SubmitHandler<Product> = async (data) => {
    setIsSaving(true);
    
    // Filter out empty bullet points
    data.marketing_bullet_points = bulletPoints.filter(bp => bp.trim() !== "");
    
    // Convert is_active to boolean if it's a string
    if (typeof data.is_active === "string") {
      data.is_active = data.is_active === "true";
    }

    try {
      const savePromise = id
        ? productAPI.updateProduct(id, data)
        : productAPI.createProduct(data);

      const savedProduct = await savePromise;
      const productId = savedProduct.id || id;

      // Save product features
      if (productId) {
        const featurePromises = Object.entries(features).map(([featureType, featureData]) => {
          return productAPI.setProductFeature(productId, {
            feature: featureType,
            is_active: featureData.is_active,
            feature_limit: featureData.feature_limit || undefined,
          } as any);
        });

        await Promise.all(featurePromises);
      }

      setIsSaving(false);
      setStatus({ type: "success", message: "Product and features saved successfully" });
      setTimeout(() => {
        navigate("/products");
      }, 2000);
    } catch (err: any) {
      setIsSaving(false);
      setStatus({ type: "error", message: "Error saving product: " + err.message });
    }
  };

  useEffect(() => {
    if (id) {
      // Load product details
      productAPI
        .getProduct(id, true)
        .then((response) => {
          reset(response);
          if (response.marketing_bullet_points && Array.isArray(response.marketing_bullet_points)) {
            setBulletPoints(response.marketing_bullet_points.length > 0 ? response.marketing_bullet_points : [""]);
          }
          // Check if stripe_info is null
          if (!response.stripe_info) {
            setStatus({ type: "error", message: "Stripe product not found" });
          }
        })
        .catch((err) => {
          setStatus({ type: "error", message: "Error loading product: " + err.message });
        });

      // Load product features
      productAPI
        .getProductFeatures(id)
        .then((productFeatures) => {
          const newFeatures: FeatureState = {
            [ProductFeatureType.MULTI_USER]: { is_active: false, feature_limit: null },
            [ProductFeatureType.SOCIAL_MEDIA]: { is_active: false, feature_limit: null },
            [ProductFeatureType.VIDEOS]: { is_active: false, feature_limit: null },
          };

          productFeatures.forEach((feature) => {
            // The backend uses "feature" as the database column name
            const featureTypeKey = feature.feature as ProductFeatureType;
            
            if (featureTypeKey && Object.values(ProductFeatureType).includes(featureTypeKey)) {
              newFeatures[featureTypeKey] = {
                is_active: Boolean(feature.is_active),
                feature_limit: feature.feature_limit ? Number(feature.feature_limit) : null,
              };
            }
          });

          setFeatures(newFeatures);
        })
        .catch((err) => {
          console.error("Error loading product features:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addBulletPoint = () => {
    setBulletPoints([...bulletPoints, ""]);
  };

  const removeBulletPoint = (index: number) => {
    const newBulletPoints = bulletPoints.filter((_, i) => i !== index);
    setBulletPoints(newBulletPoints.length > 0 ? newBulletPoints : [""]);
  };

  const updateBulletPoint = (index: number, value: string) => {
    const newBulletPoints = [...bulletPoints];
    newBulletPoints[index] = value;
    setBulletPoints(newBulletPoints);
  };

  const toggleFeature = (featureType: ProductFeatureType) => {
    setFeatures({
      ...features,
      [featureType]: {
        ...features[featureType],
        is_active: !features[featureType].is_active,
      },
    });
  };

  const updateFeatureLimit = (featureType: ProductFeatureType, limit: number | null) => {
    setFeatures({
      ...features,
      [featureType]: {
        ...features[featureType],
        feature_limit: limit,
      },
    });
  };

  const getFeatureDisplayName = (featureType: ProductFeatureType): string => {
    switch (featureType) {
      case ProductFeatureType.MULTI_USER:
        return "Multi User";
      case ProductFeatureType.SOCIAL_MEDIA:
        return "Social Media";
      case ProductFeatureType.VIDEOS:
        return "Videos";
      default:
        return featureType;
    }
  };

  return (
    <AppContainer>
      <div className="container">
        <BackButton variant="ghost" onClick={() => navigate("/products")} />
        <div className="flex justify-between">
          <h1 className="text-3xl">{id ? "Edit Product" : "Add Product"}</h1>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
            type="submit"
            form="product_form"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
        {status && (
          <div className="mt-4">
            <Alert {...status} onClose={() => setStatus(null)} />
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} id="product_form">
          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Product Name *
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("name", { required: "Product name is required" })}
                    type="text"
                    placeholder="Product Name"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="industry_type" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Industry Type *
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                    {...register("industry_type", { required: "Industry type is required" })}
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select Industry Type --</option>
                    {Object.values(IndustryType).map((type) => (
                      <option key={type} value={type}>
                        {type} {type === IndustryType.OTHER ? "[default]" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.industry_type && <p className="text-red-500 text-sm mt-1">{errors.industry_type.message}</p>}
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="full_price" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Full Price (marketing price with any discounts) *
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <span className="flex items-center pl-3 text-gray-500 sm:text-sm">$</span>
                  <input
                    {...register("full_price", { 
                      required: "Full price is required",
                      min: { value: 0, message: "Price must be positive" }
                    })}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
                {errors.full_price && <p className="text-red-500 text-sm mt-1">{errors.full_price.message}</p>}
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="stripe_product_id" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Stripe Product ID *
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("stripe_product_id", { required: "Stripe Product ID is required" })}
                    type="text"
                    placeholder="prod_xxxxxxxxxxxxx"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
                {errors.stripe_product_id && <p className="text-red-500 text-sm mt-1">{errors.stripe_product_id.message}</p>}
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="stripe_price_id" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Stripe Price ID *
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("stripe_price_id", { required: "Stripe Price ID is required" })}
                    type="text"
                    placeholder="price_xxxxxxxxxxxxx"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
                {errors.stripe_price_id && <p className="text-red-500 text-sm mt-1">{errors.stripe_price_id.message}</p>}
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="trial_period_days" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Trial Period (Days)
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    {...register("trial_period_days", { 
                      min: { value: 0, message: "Trial period must be positive" } 
                    })}
                    type="number"
                    placeholder="0"
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
                {errors.trial_period_days && <p className="text-red-500 text-sm mt-1">{errors.trial_period_days.message}</p>}
              </div>
            </div>

            <div className="sm:col-span-4">
              <label htmlFor="is_active" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Is Active *
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <select
                    {...register("is_active", { required: "Active status is required" })}
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option value="">-- Select Active Status --</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                {errors.is_active && <p className="text-red-500 text-sm mt-1">{errors.is_active.message}</p>}
              </div>
            </div>

            <div className="sm:col-span-6">
              <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                Marketing Bullet Points
              </label>
              <div className="mt-2 space-y-2">
                {bulletPoints.map((point, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={point}
                      onChange={(e) => updateBulletPoint(index, e.target.value)}
                      placeholder={`Bullet point ${index + 1}`}
                      className="block flex-1 rounded-md border-0 py-1.5 pl-3 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                    <button
                      type="button"
                      onClick={() => removeBulletPoint(index)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addBulletPoint}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Bullet Point
                </button>
              </div>
            </div>

            {/* Product Features Section */}
            {id && (
              <div className="sm:col-span-6">
                <div className="border-t border-gray-300 dark:border-gray-700 pt-6 mt-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Product Features
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Configure which features are available for this product and set limits where applicable.
                  </p>
                  
                  <div className="space-y-4">
                    {Object.values(ProductFeatureType).map((featureType) => (
                      <div
                        key={featureType}
                        className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex items-center h-5">
                            <input
                              type="checkbox"
                              checked={features[featureType]?.is_active === true}
                              onChange={() => toggleFeature(featureType)}
                              className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-base font-medium text-gray-900 dark:text-white">
                              {getFeatureDisplayName(featureType)}
                            </label>
                            <div className="mt-2 flex items-center gap-2">
                              <label className="text-sm text-gray-600 dark:text-gray-400">
                                Feature Limit:
                              </label>
                              <input
                                type="number"
                                value={features[featureType]?.feature_limit || ""}
                                onChange={(e) =>
                                  updateFeatureLimit(
                                    featureType,
                                    e.target.value ? parseInt(e.target.value) : null
                                  )
                                }
                                placeholder="Unlimited"
                                disabled={!features[featureType]?.is_active}
                                className="w-32 rounded-md border-0 py-1.5 pl-3 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                (leave empty for unlimited)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </AppContainer>
  );
};

export default ProductEdit;

